import { useEffect, useState, type ReactNode } from 'react';
import {
  currentPushSubscription,
  disableWebPush,
  enableWebPush,
  pushSupport,
  type PushClient,
} from '@bala/core';
import { Button, Card, Switch } from './primitives';
import { useToast } from './overlays';

/**
 * Activation du bandeau système (Web Push) sur l'appareil courant.
 *
 * La mécanique — interroger le serveur sur sa configuration VAPID, lire
 * l'abonnement en cours, traduire les cinq motifs d'échec en phrases — était
 * dupliquée à l'identique dans les deux applications. Seuls diffèrent le titre,
 * la description, et le fait que côté familles la carte porte en plus la
 * préférence de compte.
 */
export function PushDeviceCard({
  client,
  title,
  description,
  accountChannel,
}: {
  /** Le client HTTP de l'application : `enableWebPush` en a besoin. */
  client: PushClient;
  title: string;
  description: ReactNode;
  /**
   * Préférence de compte, quand l'application en expose une.
   *
   * Distincte de l'abonnement de l'appareil : l'une vaut pour tous les
   * appareils, l'autre pour celui-ci. Activer l'appareil allume aussi le canal,
   * sinon on s'abonne à un canal éteint et rien n'arrive jamais.
   */
  accountChannel?: { enabled: boolean; onChange: (enabled: boolean) => Promise<void> };
}) {
  const toast = useToast();
  const support = pushSupport();
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [configured, setConfigured] = useState<boolean | null>(null);

  useEffect(() => {
    void currentPushSubscription().then((subscription) => setSubscribed(Boolean(subscription)));
    client
      .get<{ configured: boolean }>('/push/vapid-public-key', { cache: false })
      .then((result) => setConfigured(result.configured))
      .catch(() => setConfigured(false));
    // Le client est stable pour la durée de vie de l'application.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activate = async () => {
    setBusy(true);
    try {
      const result = await enableWebPush(client);
      if (!result.ok) {
        const messages: Record<string, string> = {
          denied: 'Permission refusée. Réactivez-la dans les réglages du navigateur.',
          'not-configured': 'Les notifications poussées ne sont pas encore configurées sur le serveur.',
          'no-sw': "Installez l'application sur l'écran d'accueil, puis réessayez.",
          insecure: 'Les notifications exigent une connexion sécurisée (HTTPS).',
          unsupported: 'Ce navigateur ne prend pas en charge les notifications poussées.',
        };
        toast.error(messages[result.reason] ?? 'Activation impossible.');
        return;
      }
      if (accountChannel && !accountChannel.enabled) await accountChannel.onChange(true);
      setSubscribed(true);
      toast.success('Notifications activées sur cet appareil.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Activation impossible.');
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async () => {
    setBusy(true);
    try {
      await disableWebPush(client);
      setSubscribed(false);
      toast.success('Notifications coupées sur cet appareil.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Désactivation impossible.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <h3 style={{ font: 'var(--t-headline)', marginBottom: 'var(--s-2)' }}>{title}</h3>
      <p style={{ font: 'var(--t-footnote)', color: 'var(--text-secondary)', marginBottom: 'var(--s-3)' }}>
        {description}
      </p>

      {configured === false && (
        <p style={{ font: 'var(--t-caption)', color: 'var(--orange)', marginBottom: 'var(--s-3)' }}>
          Non configuré côté serveur pour le moment — les notifications restent dans l’application.
        </p>
      )}

      {!support.ok && support.reason === 'denied' && (
        <p style={{ font: 'var(--t-caption)', color: 'var(--orange)', marginBottom: 'var(--s-3)' }}>
          Permission refusée dans le navigateur. Réactivez-la dans les réglages du site.
        </p>
      )}

      {accountChannel && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-3)', marginBottom: 'var(--s-3)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ font: 'var(--t-subhead)' }}>Autoriser les bandeaux</p>
            <p style={{ font: 'var(--t-caption)', color: 'var(--text-secondary)' }}>
              Préférence du compte (tous les appareils)
            </p>
          </div>
          <Switch
            checked={accountChannel.enabled}
            onChange={(next) => void accountChannel.onChange(next)}
            label="Autoriser les bandeaux"
          />
        </div>
      )}

      <Button
        variant={subscribed ? 'secondary' : 'primary'}
        loading={busy}
        disabled={configured === false || (!support.ok && support.reason !== 'denied')}
        onClick={() => void (subscribed ? deactivate() : activate())}
      >
        {subscribed ? 'Désactiver sur cet appareil' : 'Activer sur cet appareil'}
      </Button>
    </Card>
  );
}
